"use client";
import { Briefcase, Calendar, Mail, Phone, User } from "lucide-react";
import moment from "moment";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/dialogue/radix-dialog";
import { Badge } from "src/components/ui/Badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import { Employee } from "src/redux/types/_employee.type";

interface EmployeeDetailsDialogueProps {
  children: React.ReactNode;
  employee: Employee;
}

const EmployeeDetailsDialogue = ({
  children,
  employee,
}: EmployeeDetailsDialogueProps) => {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent
        className="max-w-4xl rounded-2xl p-0 bg-card flex flex-col"
        style={{ maxHeight: "80vh" }}
      >
        {/* Header fixed */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-semibold text-gray-800">
            Employee Details
          </DialogTitle>
        </DialogHeader>

        <div
          className="overflow-y-auto px-6 py-4 space-y-6"
          style={{ maxHeight: "calc(80vh - 70px)" }}
        >
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            <Card className="shadow-md rounded-2xl border border-gray-200">
              <CardHeader className="flex flex-col sm:flex-row items-center gap-4">
                <div className="h-20 w-20 rounded-full bg-gradient-to-tr from-indigo-400 to-blue-600 flex items-center justify-center text-white text-3xl font-bold">
                  {employee.full_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl font-semibold capitalize">
                    {employee.full_name}
                  </CardTitle>
                  <p className="text-sm text-muted">
                    {employee.designation} · {employee.department}
                  </p>
                  <p className="text-sm text-muted my-1">
                    Reporting Officer: {employee.reporting_manager_name }
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge className="bg-green-100 text-">
                      {employee.employeement_type}
                    </Badge>
                    <Badge>{employee.role}</Badge>
                    {/* {employee.employeement_type === "CONFIRMED" ? (
                      <Badge className="bg-amber-100 text-">
                        {employee.duration}
                      </Badge>
                    ): (
                      <span>{ employee.end_date && formatDate(employee.end_date)}</span>
                    )} */}
                  </div>
                </div>
              </CardHeader>

              <div className="border-t-2 h-1 border-gray-300" />

              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {employee.email}
                  </p>
                  <p className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {employee.phone_number}
                  </p>
                  <p className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    {employee.gender}{" "}
                    {employee.marital_status && `· ${employee.marital_status}`}
                  </p>
                </div>

                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    Employee Id: {employee.employee_code}
                  </p>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Joined: {moment(employee.date_of_joining).format("DD-MM-YYYY")}
                  </p>
                  {employee.end_date && (
                    <p className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {employee.employeement_type === "CONFIRMED"
                        ? "Confirmed Date "
                        : "Probation Ends "}{" "}
                      : {moment(employee.end_date).format("DD-MM-YYYY")}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">States & Districts</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {employee.state?.length > 0 ? (
                  <>
                    <p>
                      <strong>State:</strong>{" "}
                      {employee.state.map((s) => s.state).join(", ")}
                    </p>
                    {employee.district?.length > 0 && (
                      <p>
                        <strong>Districts:</strong>{" "}
                        {employee.district.map((d) => d.district).join(", ")}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground italic">
                    No state/district info
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-6 sm:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Address</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <p>
                    <strong>Permanent:</strong> {employee.permanent_address}
                  </p>
                  <p>
                    <strong>Current:</strong> {employee.current_address}
                  </p>
                  <p>
                    <strong>PIN:</strong> {employee.pin}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Bank Info</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <p>
                    <strong>Bank:</strong> {employee.bank_name}
                  </p>
                  <p>
                    <strong>Branch:</strong> {employee.branch}
                  </p>
                  <p>
                    <strong>Account:</strong> {employee.bank_ac_number}
                  </p>
                  <p>
                    <strong>IFSC:</strong> {employee.ifsc_number}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Identification</CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4 text-sm">
                <p>
                  <strong>Aadhar:</strong> {employee.adharcard_number}
                </p>
                <p>
                  <strong>PAN:</strong> {employee.pan_card_number}
                </p>
                {employee.passport_number && (
                  <p>
                    <strong>Passport:</strong> {employee.passport_number}
                  </p>
                )}
                {employee.pf_number && (
                  <p>
                    <strong>PF Number:</strong> {employee.pf_number}
                  </p>
                )}
                {employee.uan_number && (
                  <p>
                    <strong>UAN Number:</strong> {employee.uan_number}
                  </p>
                )}
                {employee.esic_uan && (
                  <p>
                    <strong>ESIC UAN:</strong> {employee.esic_uan}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {employee.emergency_contact_name ? (
                  <>
                    <p>
                      <strong>Name:</strong> {employee.emergency_contact_name}
                    </p>
                    <p>
                      <strong>Relationship:</strong>{" "}
                      {employee.emergency_contact_relationship}
                    </p>
                    <p>
                      <strong>Number:</strong>{" "}
                      {employee.emergency_contact_number}
                    </p>
                    <p>
                      <strong>Address:</strong>{" "}
                      {employee.emergency_contact_address}
                    </p>
                  </>
                ) : (
                  <p className="text-muted-foreground italic">
                    No emergency contact provided
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeDetailsDialogue;
